import { createContext, useContext, useState } from "react";
import { auth } from "../firebase/config";
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase/config'; // Firebase Functions
import { toast } from "react-toastify";

export const RegisterContext = createContext()

export const useRegisterContext = () => {
    return useContext(RegisterContext)
}

export const RegisterProvider = ({children}) => {
    const [reloadRegister, setReloadRegister] = useState(false);
    const [message, setMessage] = useState(null);

    // Función para solicitar una recarga
    const triggerReloadUser = () => {
        setReloadRegister(prev => !prev); // Cambiar el estado para disparar la recarga
    };

    // Cambios de clave para usuarios con login
    const changePassword = async (newPassword) => {
        try {
            // Llamada a la función de Firebase Functions
            const changePasswordFunction = httpsCallable(functions, 'changePassword');
            const result = await changePasswordFunction({ newPassword });
            toast.success("Contraseña actualizada con éxito.");
            return result.data;
        } catch (error) {
            toast.error("Error al cambiar la contraseña: " + error.message);
            throw error;
        }
    };

    // Registros de mensanjeros
    const registerMessenger = async (values) => {
        setReloadRegister(true);
        try {
            if (!auth.currentUser) {
            throw new Error("Debes iniciar sesión para registrar usuarios.");
            }

            // Asegura payload 100% JSON-serializable (evita File/Blob/undefined/etc.)
            const safeValues = JSON.parse(JSON.stringify(values || {}));

            const registerUserFunction = httpsCallable(functions, "functionsRegisterUserMessenger");
            const res = await registerUserFunction(safeValues);
            const payload = res?.data;

            if (!payload?.success || !payload?.id) {
                const msg = payload?.message || "No se pudo registrar el mensajero.";
                const e = new Error(msg);
                // preserva detalles para diagnóstico
                e.code = payload?.code || res?.error?.code;
                e.details = payload?.details || res?.error?.details || null;
                throw e;
            }

            const id = payload.id; // uid
            const userDoc = payload.data || {};
            const warnings = payload.warnings || [];
            const directUrl = payload.directUrl || null;

            setMessage({
                type: "success",
                text: "Usuario mensajero con éxito" + (warnings.length ? " (con advertencias)" : ""),
            });

            // Devuelve todo lo útil para hidratar el front inmediatamente
            return { id, data: userDoc, warnings, directUrl };
        } catch (err) {
            // Mensaje base
            let text = err?.message || "Error registrando usuario";
            // Si viene paso desde el backend (HttpsError.details.step)
            const step = err?.details?.step;
            if (step) text += ` (paso: ${step})`;
            // Si viene código de Firebase Functions
            if (err?.code) text += ` [${err.code}]`;

            console.error("register() error:", err);
            setMessage({ type: "error", text });
            return null;
        } finally {
            setReloadRegister(false);
        }
    };

    // Registros de nuevos usuarios
    const register = async (values) => {
        setReloadRegister(true);
        try {
            if (!auth.currentUser) {
            throw new Error("Debes iniciar sesión para registrar usuarios.");
            }

            // Asegura payload 100% JSON-serializable (evita File/Blob/undefined/etc.)
            const safeValues = JSON.parse(JSON.stringify(values || {}));

            const registerUserFunction = httpsCallable(functions, "functionsRegisterUser");
            const res = await registerUserFunction(safeValues);
            const payload = res?.data;

            if (!payload?.success || !payload?.id) {
                const msg = payload?.message || "No se pudo registrar el usuario.";
                const e = new Error(msg);
                // preserva detalles para diagnóstico
                e.code = payload?.code || res?.error?.code;
                e.details = payload?.details || res?.error?.details || null;
                throw e;
            }

            const id = payload.id; // uid
            const codeOrden = payload.codeOrden ?? payload.data?.code_orden ?? null;
            const userDoc = payload.data || {};
            const warnings = payload.warnings || [];
            const directUrl = payload.directUrl || null;

            setMessage({
                type: "success",
                text: "Usuario registrado con éxito" + (warnings.length ? " (con advertencias)" : ""),
            });

            // Devuelve todo lo útil para hidratar el front inmediatamente
            return { id, codeOrden, data: userDoc, warnings, directUrl };
        } catch (err) {
            // Mensaje base
            let text = err?.message || "Error registrando usuario";
            // Si viene paso desde el backend (HttpsError.details.step)
            const step = err?.details?.step;
            if (step) text += ` (paso: ${step})`;
            // Si viene código de Firebase Functions
            if (err?.code) text += ` [${err.code}]`;

            console.error("register() error:", err);
            setMessage({ type: "error", text });
            return null;
        } finally {
            setReloadRegister(false);
        }
    };

    // Registros de nuevos usuarios para carga sin notificacion
    const registerProv = async (values) => {
        setReloadRegister(true);
        try {
            if (!auth.currentUser) {
                throw new Error("Debes iniciar sesión para registrar usuarios.");
            }
            // Asegura payload 100% JSON-serializable (evita File/Blob/undefined/etc.)
            const safeValues = JSON.parse(JSON.stringify(values || {}));
            const registerUserFunction = httpsCallable(functions, "functionsRegisterUserProv");
            const res = await registerUserFunction(safeValues);
            const payload = res?.data;
            if (!payload?.success || !payload?.id) {
                const msg = payload?.message || "No se pudo registrar el usuario.";
                const e = new Error(msg);
                // preserva detalles para diagnóstico
                e.code = payload?.code || res?.error?.code;
                e.details = payload?.details || res?.error?.details || null;
                throw e;
            }
            const id = payload.id;        // uid generado en Auth
            const userDoc = payload.data || {}; // documento en Firestore
            const warnings = payload.warnings || [];
            setMessage({
                type: "success",
                text: "Usuario registrado con éxito" + (warnings.length ? " (con advertencias)" : ""),
            });
            // Devuelve lo útil para el frontend
            return { id, data: userDoc, warnings };
        } catch (err) {
            // Mensaje base
            let text = err?.message || "Error registrando usuario";
            // Si viene paso desde el backend (HttpsError.details.step)
            const step = err?.details?.step;
            if (step) text += ` (paso: ${step})`;
            // Si viene código de Firebase Functions
            if (err?.code) text += ` [${err.code}]`;
            console.error("registerProv() error:", err);
            setMessage({ type: "error", text });
            return null;
        } finally {
            setReloadRegister(false);
        }
    };

    // Contexto: función limpia que solo ejecuta y retorna
    const registerUsersMassively = async (data) => {
        setReloadRegister(true);
        try {
            if (!auth.currentUser) {
            throw new Error("Debes iniciar sesión para registrar usuarios.");
            }

            // Asegura payload 100% JSON-serializable (evita File/Blob/undefined/etc.)
            const safeValues = JSON.parse(JSON.stringify(data|| {}));

            const registerUserFunction = httpsCallable(functions, "functionsRegisterUser");
            const res = await registerUserFunction(safeValues);
            const payload = res?.data;

            if (!payload?.success || !payload?.id) {
                const msg = payload?.message || "No se pudo registrar el usuario.";
                const e = new Error(msg);
                // preserva detalles para diagnóstico
                e.code = payload?.code || res?.error?.code;
                e.details = payload?.details || res?.error?.details || null;
                throw e;
            }

            const id = payload.id; // uid
            const codeOrden = payload.codeOrden ?? payload.data?.code_orden ?? null;
            const userDoc = payload.data || {};
            const warnings = payload.warnings || [];
            const directUrl = payload.directUrl || null;

            setMessage({
                type: "success",
                text: "Usuario registrado con éxito" + (warnings.length ? " (con advertencias)" : ""),
            });

            // Devuelve todo lo útil para hidratar el front inmediatamente
            return { id, codeOrden, data: userDoc, warnings, directUrl };
        } catch (err) {
            // Mensaje base
            let text = err?.message || "Error registrando usuario";
            // Si viene paso desde el backend (HttpsError.details.step)
            const step = err?.details?.step;
            if (step) text += ` (paso: ${step})`;
            // Si viene código de Firebase Functions
            if (err?.code) text += ` [${err.code}]`;

            console.error("register() error:", err);
            setMessage({ type: "error", text });
            return null;
        } finally {
            setReloadRegister(false);
        }
    };
  
    // Registros masivo de usuarios para carga sin notificacion
    const registerUsersMassivelyProv = async (users) => {
        setReloadRegister(true);
        try {
            const registerUsersSend = httpsCallable(functions, 'registerUsersSendProv');
            const result = await registerUsersSend({ users });
            // Manejo de la respuesta de la función
            if (result.data.success) {
                toast.success('Usuarios registrados exitosamente.');
            } else {
                console.log('Algunos usuarios no se pudieron registrar.');
            }
        } catch (error) {
            console.error('Error al registrar usuarios:', error);
            toast.error(`Error al registrar usuarios: ${error.message}`);
        } finally {
            setReloadRegister(false);
        }
    };

    // Registros invitaciones masivas
    const registerUsersMassivelyOpen = async (users) => {
        setReloadRegister(true);
        try {
            const registerUsersSendOpen = httpsCallable(functions, 'registerUsersSendOpen');
            const result = await registerUsersSendOpen({ users });
            // Manejo de la respuesta de la función
            if (result.data.success) {
                toast.success('Usuarios registrados exitosamente.');
            } else {
                console.log('Algunos usuarios no se pudieron registrar.');
            }
        } catch (error) {
            console.error('Error al registrar usuarios:', error);
            toast.error(`Error al registrar usuarios: ${error.message}`);
        } finally {
            setReloadRegister(false);
        }
    };

    // Verifica la validez del token (seguro, v2)
    const verifyToken = async (email, token) => {
        try {
            const cleanEmail = decodeURIComponent(String(email || "")).trim().toLowerCase();
            const cleanToken = String(token || "").trim();
            if (!cleanEmail || !cleanToken) {
                return { success: false, message: "Faltan email o token." };
            }
            const verifyTokenFn = httpsCallable(functions, "verifyTokenAndGetTemplate");
            const { data } = await verifyTokenFn({ email: cleanEmail, token: cleanToken });
            return data; // { success: true } o { success:false, message }
        } catch (err) {
            const code = err?.code || "";
            let msg = err?.message || "Error verificando el token.";
            if (code.includes("permission-denied")) msg = "Token inválido.";
            if (code.includes("deadline-exceeded")) msg = "El token ha expirado.";
            if (code.includes("unauthenticated")) msg = "No autorizado. (Revisa App Check)";
            return { success: false, message: msg };
        }
    };

    // Restablecer contraseña con token (desde el link del correo)
    const sendPasswordReset = async (email, token, newPassword) => {
        try {
            const cleanEmail = decodeURIComponent(String(email || "")).trim().toLowerCase();
            const cleanToken = String(token || "").trim();
            const pwd        = String(newPassword || "");

            if (!cleanEmail || !cleanToken || !pwd) {
                throw new Error("Email, token y nueva contraseña son obligatorios.");
            }
            if (pwd.length < 8) {
                throw new Error("La contraseña debe tener al menos 8 caracteres.");
            }

            const updateUserPassword = httpsCallable(functions, "updateUserPassword");
            const { data } = await updateUserPassword({
            email: cleanEmail,
            token: cleanToken,
            newPassword: pwd,
            });

            if (data?.success) {
                toast.success(data?.message || "Contraseña actualizada correctamente.");
                return { success: true, message: data?.message || "Contraseña actualizada correctamente." };
            }

            const msg = data?.message || "No se pudo actualizar la contraseña.";
            toast.error(msg);
            return { success: false, message: msg };
        } catch (error) {
            const code = error?.code || "";
            let msg = error?.message || "Error desconocido";
            if (code.includes("permission-denied")) msg = "Token inválido o ya usado.";
            if (code.includes("deadline-exceeded")) msg = "El token ha expirado.";
            if (code.includes("unauthenticated")) msg = "No autorizado. (Revisa App Check)";
            toast.error(`Error al actualizar la contraseña: ${msg}`);
            return { success: false, message: msg };
        }
    };

    return (
        <RegisterContext.Provider value={{
            register,
            registerMessenger,
            message,
            setMessage,
            sendPasswordReset,
            changePassword,
            registerUsersMassively,
            registerUsersMassivelyOpen,
            registerProv,
            registerUsersMassivelyProv,
            verifyToken,
            // reccargar informacion
            triggerReloadUser,
            reloadRegister 
            }}>
            {children}
        </RegisterContext.Provider>
    )
}